import {
  getMyInstancesForRegion,
  SessionExpiredError,
  UserInstance,
} from "@/api/instance.ts";
import InventoZen from "@/components/icons/InventoZen.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { REGIONS } from "@/config/regions";
import { useAuth } from "@/context/auth/AuthContext";
import { error, toast } from "@/hooks/use-toast";
import { fetchMainLanguage } from "@/i18n";
import { resolveTokenForRegion } from "@/lib/auth-tokens";
import { RegionInstancesFetchError } from "@/lib/instance-errors";
import { RegionCode } from "@/lib/axiosConfig";
import { cn } from "@/lib/utils";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useNavigate } from "react-router-dom";

interface TaggedUserInstance extends UserInstance {
  regionCode: RegionCode;
}

type LoadStatus =
  | "idle"
  | "loading"
  | "ready"
  | "no_tokens"
  | "session_expired"
  | "empty"
  | "error";

function encodeSelectValue(
  regionCode: RegionCode,
  instanceName: string,
): string {
  return `${regionCode}:${instanceName}`;
}

function decodeSelectValue(value: string): {
  regionCode: RegionCode;
  instanceName: string;
} {
  const colonIdx = value.indexOf(":");
  return {
    regionCode: value.slice(0, colonIdx),
    instanceName: value.slice(colonIdx + 1),
  };
}

/**
 * Palette tournante : chaque région prend la n-ième classe selon son ordre
 * dans REGIONS. Évite de hardcoder un mapping par code de région.
 */
const REGION_BADGE_CLASSES = [
  "bg-sky-100 text-sky-700 border-sky-200",
  "bg-violet-100 text-violet-700 border-violet-200",
  "bg-amber-100 text-amber-700 border-amber-200",
  "bg-emerald-100 text-emerald-700 border-emerald-200",
  "bg-rose-100 text-rose-700 border-rose-200",
];

const Instance = () => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { regionTokens, setInstance: setInstanceGlobal, logout } = useAuth();
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [mergedInstances, setMergedInstances] = useState<TaggedUserInstance[]>(
    [],
  );
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("idle");
  const [failedRegionLabels, setFailedRegionLabels] = useState<string[]>([]);
  const navigation = useNavigate();
  const autoSelectedRef = useRef(false);

  const backgroundImageUrl = t("loginBackgroundImageUrl", { defaultValue: "" });
  const isLoadingInstances = loadStatus === "loading";

  /** Charge l'instance choisie puis entre dans l'app. */
  const selectInstance = useCallback(
    (regionCode: RegionCode, instanceName: string) => {
      setInstanceGlobal(instanceName, regionCode);
      fetchMainLanguage();
      navigation("/dashboard");
    },
    [setInstanceGlobal, navigation],
  );

  const selectInstanceRef = useRef(selectInstance);
  selectInstanceRef.current = selectInstance;

  const redirectToLoginWithExpiredSession = useCallback(() => {
    toast(
      error({
        title: vt("instance.sessionExpired.title", "Session expirée"),
        description: vt(
          "instance.sessionExpired.description",
          "Veuillez vous reconnecter.",
        ),
        duration: 5000,
      }),
    );
    logout();
  }, [logout, vt]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const regionsToFetch = REGIONS.flatMap((region) => {
      const token = resolveTokenForRegion(region.code, regionTokens);
      if (!token) return [];
      return [{ region, token }];
    });

    if (regionsToFetch.length === 0) {
      setLoadStatus("no_tokens");
      setMergedInstances([]);
      setFailedRegionLabels([]);
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    setLoadStatus("loading");
    setFailedRegionLabels([]);
    setMergedInstances([]);

    const fetches = regionsToFetch.map(({ region, token }) =>
      getMyInstancesForRegion(token, region.code, controller.signal)
        .then((items) => ({
          kind: "ok" as const,
          regionCode: region.code,
          regionLabel: region.label,
          items,
        }))
        .catch((err) => {
          if (err instanceof SessionExpiredError) {
            return {
              kind: "session_expired" as const,
              regionCode: region.code,
            };
          }
          if (err instanceof RegionInstancesFetchError) {
            console.warn(
              `[instance] region ${region.code} (${region.baseUrl}) failed:`,
              err.message,
            );
          } else if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
            console.warn(
              `[instance] region ${region.code} (${region.baseUrl}) failed:`,
              err?.message ?? err,
            );
          }
          return {
            kind: "error" as const,
            regionCode: region.code,
            regionLabel: region.label,
          };
        }),
    );

    Promise.all(fetches).then((results) => {
      if (cancelled) return;

      const expiredCount = results.filter(
        (r) => r.kind === "session_expired",
      ).length;
      if (expiredCount > 0 && expiredCount === results.length) {
        setLoadStatus("session_expired");
        redirectToLoginWithExpiredSession();
        return;
      }

      const failedLabels = results
        .filter((r) => r.kind === "error")
        .map((r) => r.regionLabel);
      setFailedRegionLabels(failedLabels);

      const flat = results
        .filter((r) => r.kind === "ok")
        .flatMap((r) =>
          r.items.map((item) => ({ ...item, regionCode: r.regionCode })),
        );
      const sorted = flat.sort((a, b) =>
        a.instance.name.localeCompare(b.instance.name),
      );

      if (sorted.length === 1) {
        if (!autoSelectedRef.current) {
          autoSelectedRef.current = true;
          selectInstanceRef.current(
            sorted[0].regionCode,
            sorted[0].instance.name,
          );
        }
        return;
      }

      if (sorted.length === 0) {
        setLoadStatus(failedLabels.length > 0 ? "error" : "empty");
        setMergedInstances([]);
        return;
      }

      setMergedInstances(sorted);
      setLoadStatus("ready");
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [regionTokens, redirectToLoginWithExpiredSession]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedValue) return;

    const { regionCode, instanceName } = decodeSelectValue(selectedValue);
    selectInstance(regionCode, instanceName);
  };

  const showSelect =
    loadStatus === "ready" ||
    loadStatus === "idle" ||
    (loadStatus === "error" && mergedInstances.length > 0);

  const statusMessage = (() => {
    switch (loadStatus) {
      case "no_tokens":
        return vt(
          "instance.noTokens",
          "Aucune session active trouvée. Veuillez vous reconnecter.",
        );
      case "empty":
        return vt(
          "instance.noInstances",
          "Aucune instance ne vous est attribuée. Contactez votre administrateur.",
        );
      case "error":
        return vt(
          "instance.loadError",
          "Impossible de charger vos instances. Réessayez ou reconnectez-vous.",
        );
      case "session_expired":
        return vt(
          "instance.sessionExpired.description",
          "Veuillez vous reconnecter.",
        );
      default:
        return null;
    }
  })();

  return (
    <div className="w-full h-full justify-between items-center inline-flex">
      <div className="w-full h-screen relative">
        <div className="h-full flex items-center justify-center">
          <div className="m-auto w-[409px] h-fit p-10 bg-white rounded-lg shadow border border-slate-200 flex-col justify-start items-center gap-8 inline-flex">
            <div>
              <div className="justify-center items-center flex">
                <InventoZen />
              </div>
              <div className="text-center text-slate-900 text-3xl font-semibold font-['Instrument Sans'] leading-loose">
                {t("instanceChoice")}
              </div>
            </div>
            <form onSubmit={handleSubmit} className="w-full grid">
              {failedRegionLabels.length > 0 && mergedInstances.length > 0 && (
                <p className="text-amber-700 text-sm mb-3 text-left">
                  {t("instance.partialRegionFailure", {
                    defaultValue:
                      "Certaines régions sont indisponibles : {{regions}}",
                    regions: failedRegionLabels.join(", "),
                  })}
                </p>
              )}
              {statusMessage && (
                <p className="text-red-600 text-sm mb-3 text-left">
                  {statusMessage}
                </p>
              )}
              {showSelect && (
                <>
                  <Label
                    className="text-start font-['Raleway'] mb-1.5"
                    htmlFor="instance"
                  >
                    {t("instance")}
                  </Label>
                  <Select
                    onValueChange={(value) => setSelectedValue(value)}
                    defaultValue={selectedValue}
                    disabled={isLoadingInstances}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          isLoadingInstances
                            ? vt("loading", "Chargement...")
                            : t("selectAnInstance")
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {mergedInstances.map((elt, index) => {
                        const regionIdx = REGIONS.findIndex(
                          (r) => r.code === elt.regionCode,
                        );
                        const region = REGIONS[regionIdx];
                        const badgeClass =
                          REGION_BADGE_CLASSES[
                            regionIdx % REGION_BADGE_CLASSES.length
                          ];
                        return (
                          <SelectItem
                            className="cursor-pointer"
                            key={`${elt.regionCode}_${elt.instance.name}_${index}`}
                            value={encodeSelectValue(
                              elt.regionCode,
                              elt.instance.name,
                            )}
                          >
                            <div className="flex items-center gap-2 w-full">
                              <span>{elt.instance.name}</span>
                              <span className="text-slate-500">
                                ({elt.role})
                              </span>
                              <span
                                className={cn(
                                  "ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded border uppercase tracking-wide",
                                  badgeClass,
                                )}
                                title={region?.baseUrl}
                              >
                                {region?.label ?? elt.regionCode}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </>
              )}
              {isLoadingInstances && !showSelect && (
                <p className="text-slate-600 text-sm text-center">
                  {vt("loading", "Chargement...")}
                </p>
              )}
              {loadStatus === "no_tokens" ||
              loadStatus === "error" ||
              loadStatus === "empty" ? (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-8 h-12 px-4 py-3 bg-[#008bd4] rounded-lg border border-[#006eab] justify-center items-center gap-1.5 inline-flex"
                  onClick={() => logout()}
                >
                  <div className="px-0.5 justify-center items-center flex">
                    <p className="text-white text-base font-semibold font-['Raleway'] leading-normal">
                      {vt("instance.reconnect", "Se reconnecter")}
                    </p>
                  </div>
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={!selectedValue || isLoadingInstances}
                  variant="outline"
                  className="mt-8 h-12 px-4 py-3 bg-[#008bd4] rounded-lg border border-[#006eab] justify-center items-center gap-1.5 inline-flex disabled:opacity-50"
                >
                  <div className="px-0.5 justify-center items-center flex">
                    <p className="text-white text-base font-semibold font-['Raleway'] leading-normal">
                      {t("continue")}
                    </p>
                  </div>
                </Button>
              )}
            </form>
          </div>
        </div>
        <div className="absolute bottom-0 w-full h-16 px-12 py-5 bg-white border-t border-slate-200 flex justify-between items-center">
          <div className="text-center text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal">
            {`${t("copyrightInventoZen")} | ${t("version")}: ${import.meta.env.VITE_CURRENT_GIT_SHA}`}
          </div>
          <div className="justify-start items-start gap-5 flex">
            <a
              href={"#"}
              className="text-[#006eab] text-sm font-bold font-['Raleway'] leading-normal"
            >
              {t("CGV")}
            </a>
            <div className="text-center text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal">
              |
            </div>
            <a
              href={"#"}
              className="text-[#006eab] text-sm font-bold font-['Raleway'] leading-normal"
            >
              {t("footerLegalNotices")}
            </a>
            <div className="text-center text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal">
              |
            </div>
            <p className="text-[#006eab] text-sm font-bold font-['Raleway'] leading-normal">
              {t("optimizeForChrome")}
            </p>
          </div>
        </div>
      </div>
      <div
        className={cn(
          "h-screen relative md:hidden lg:block transition-all duration-300",
          backgroundImageUrl ? "w-[50%]" : "w-0",
        )}
      >
        {backgroundImageUrl && (
          <img
            src={backgroundImageUrl}
            alt="login"
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute bottom-0 left-0 w-full flex-col justify-center items-center inline-flex">
          <div className="self-stretch p-8 bg-white/10 border-t border-white/10 backdrop-blur-xl flex-col justify-start items-start gap-8 flex">
            <div className="h-14 flex-col justify-start items-start gap-1 flex">
              <p className="grow shrink basis-0 text-white text-xl font-semibold font-['Instrument Sans'] leading-normal text-left">
                {t("titleOfTheWork")}
              </p>
              <p className="self-stretch text-white text-lg font-normal font-['Raleway'] leading-normal text-left">
                {t("artistFirstPage")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Instance;
